import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Grid,
  TextField,
  InputAdornment,
  Collapse
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterListIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import { effectiveMerchantName } from '../utils/effectiveMerchant';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  fitTruncateSx,
  fitChipSx,
} from '../utils/fitTable';

const RevolutLinking: React.FC = () => {
  const [predictions, setPredictions] = useState<any[]>([]);
  const [confirmedLinks, setConfirmedLinks] = useState<any[]>([]);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [linkingError, setLinkingError] = useState<string>('');
  const [expandedSections, setExpandedSections] = useState({
    predictions: true,
    confirmed: true
  });
  
  // Filtering and sorting state
  const [filters, setFilters] = useState<{
    description?: string;
    amount?: string;
    date?: string;
    confidence?: string;
  }>({});
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  // Fetch predictions for linking tab
  const fetchPredictions = async () => {
    try {
      console.log('[REVOLUT LINKING] Fetching predictions...');
      setLoadingPredictions(true);
      setLinkingError('');
      // Increase timeout to 120 seconds for predictions endpoint (can be slow with large datasets)
      const response = await api.get('/api/revolut/transaction-expense-links/predictions', {
        timeout: 120000  // 120 seconds
      });
      setPredictions(response.data.predictions || []);
      console.log('[REVOLUT LINKING] Fetched predictions:', response.data.predictions?.length || 0);
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to fetch predictions';
      console.error('[REVOLUT LINKING] Error fetching predictions:', err);
      if (err.code === 'ECONNABORTED') {
        setLinkingError('Request timed out. The predictions calculation is taking longer than expected. Try again or contact support if this persists.');
      } else {
        setLinkingError(errorMsg);
      }
    } finally {
      setLoadingPredictions(false);
    }
  };

  // Fetch confirmed links
  const fetchConfirmedLinks = async () => {
    try {
      console.log('[REVOLUT LINKING] Fetching confirmed links...');
      const response = await api.get('/api/revolut/transaction-expense-links?status=confirmed');
      setConfirmedLinks(response.data.links || []);
      console.log('[REVOLUT LINKING] Fetched confirmed links:', response.data.links?.length || 0);
    } catch (err: any) {
      console.error('[REVOLUT LINKING] Error fetching confirmed links:', err);
    }
  };

  // Confirm a match
  const confirmMatch = async (prediction: any) => {
    try {
      setLinkingError('');
      console.log('[REVOLUT LINKING] Confirming match:', prediction.uid);
      const response = await api.post('/api/revolut/transaction-expense-links', {
        transaction_revolut_id: prediction.transaction_revolut_id,
        expense_revolut_id: prediction.expense_revolut_id,
        uid: prediction.uid,
        confidence_score: prediction.confidence_score
      });
      
      console.log('[REVOLUT LINKING] Match confirmed:', response.data);
      
      // Refresh predictions and confirmed links
      await fetchPredictions();
      await fetchConfirmedLinks();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to confirm match';
      console.error('[REVOLUT LINKING] Error confirming match:', err);
      setLinkingError(errorMsg);
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchPredictions();
    fetchConfirmedLinks();
  }, []);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount: number | string | null, currency: string | null) => {
    if (amount === null || amount === undefined) return 'N/A';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: currency || 'EUR'
    }).format(numAmount);
  };

  // Calculate summary statistics
  const summary = useMemo(() => {
    const highConfidence = predictions.filter(p => p.confidence_score >= 0.7).length;
    const mediumConfidence = predictions.filter(p => p.confidence_score >= 0.5 && p.confidence_score < 0.7).length;
    const lowConfidence = predictions.filter(p => p.confidence_score < 0.5).length;
    const avgConfidence = predictions.length > 0
      ? predictions.reduce((sum, p) => sum + p.confidence_score, 0) / predictions.length
      : 0;

    return {
      totalPredictions: predictions.length,
      totalConfirmed: confirmedLinks.length,
      highConfidence,
      mediumConfidence,
      lowConfidence,
      avgConfidence
    };
  }, [predictions, confirmedLinks]);

  // Filter predictions
  const filteredPredictions = useMemo(() => {
    let filtered = [...predictions];

    if (filters.description) {
      const searchTerm = filters.description.toLowerCase();
      filtered = filtered.filter(p => 
        (p.transaction?.description || '').toLowerCase().includes(searchTerm) ||
        (p.expense?.description || '').toLowerCase().includes(searchTerm) ||
        effectiveMerchantName(p.transaction).toLowerCase().includes(searchTerm) ||
        (p.expense?.merchant || '').toLowerCase().includes(searchTerm)
      );
    }

    if (filters.amount) {
      const amountFilter = parseFloat(filters.amount);
      if (!isNaN(amountFilter)) {
        filtered = filtered.filter(p => {
          const txAmount = Math.abs(parseFloat(p.transaction?.amount || '0'));
          const expAmount = Math.abs(parseFloat(p.expense?.spent_amount_value || '0'));
          return Math.abs(txAmount - amountFilter) < 0.01 || Math.abs(expAmount - amountFilter) < 0.01;
        });
      }
    }

    if (filters.confidence) {
      const confFilter = parseFloat(filters.confidence) / 100;
      if (!isNaN(confFilter)) {
        filtered = filtered.filter(p => {
          if (filters.confidence?.includes('>')) {
            return p.confidence_score >= confFilter;
          } else if (filters.confidence?.includes('<')) {
            return p.confidence_score <= confFilter;
          }
          return Math.abs(p.confidence_score - confFilter) < 0.05;
        });
      }
    }

    // Apply sorting
    if (sortConfig) {
      filtered.sort((a, b) => {
        let aVal: any, bVal: any;
        
        if (sortConfig.key === 'confidence') {
          aVal = a.confidence_score;
          bVal = b.confidence_score;
        } else if (sortConfig.key === 'date') {
          aVal = new Date(a.transaction?.completed_at || a.transaction?.created_at || 0).getTime();
          bVal = new Date(b.transaction?.completed_at || b.transaction?.created_at || 0).getTime();
        } else if (sortConfig.key === 'amount') {
          aVal = Math.abs(parseFloat(a.transaction?.amount || '0'));
          bVal = Math.abs(parseFloat(b.transaction?.amount || '0'));
        } else {
          aVal = a[sortConfig.key] || '';
          bVal = b[sortConfig.key] || '';
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [predictions, filters, sortConfig]);

  // Filter confirmed links
  const filteredConfirmedLinks = useMemo(() => {
    let filtered = [...confirmedLinks];

    if (filters.description) {
      const searchTerm = filters.description.toLowerCase();
      filtered = filtered.filter(link => 
        (link.transaction_uid || '').toLowerCase().includes(searchTerm) ||
        (link.expense_uid || '').toLowerCase().includes(searchTerm)
      );
    }

    return filtered;
  }, [confirmedLinks, filters]);

  const handleFilter = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const toggleSection = (section: 'predictions' | 'confirmed') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const SortableHeader: React.FC<{
    children: React.ReactNode;
    sortKey: string;
  }> = ({ children, sortKey }) => {
    const isActive = sortConfig?.key === sortKey;
    const direction = isActive ? sortConfig.direction : null;
    
    return (
      <TableCell 
        sx={{ 
          cursor: 'pointer', 
          userSelect: 'none',
          '&:hover': { backgroundColor: 'action.hover' },
          fontWeight: 'bold'
        }}
        onClick={() => handleSort(sortKey)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {children}
          {isActive && (
            direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
          )}
        </Box>
      </TableCell>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Transaction & Expense Linking
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchPredictions();
            fetchConfirmedLinks();
          }}
          disabled={loadingPredictions}
        >
          Refresh
        </Button>
      </Box>

      {linkingError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {linkingError}
        </Alert>
      )}

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="primary">
              {summary.totalPredictions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Predictions
            </Typography>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="success.main">
              {summary.totalConfirmed}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Confirmed Links
            </Typography>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="success.main">
              {summary.highConfidence}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              High Confidence (≥70%)
            </Typography>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="warning.main">
              {summary.mediumConfidence}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Medium Confidence (50-70%)
            </Typography>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              {(summary.avgConfidence * 100).toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Avg Confidence
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Predictions Section */}
      <Card sx={{ mb: 3 }} id="predictions">
        <Box sx={{ p: 2, backgroundColor: 'grey.50', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={() => toggleSection('predictions')}
              size="small"
            >
              {expandedSections.predictions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
              Predicted Matches ({summary.totalPredictions})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
              - Potential matches between transactions and expenses
            </Typography>
          </Box>
        </Box>
        <Collapse in={expandedSections.predictions}>
          <Box sx={{ p: 2 }}>
            {/* Filters */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Filter by description or merchant..."
                value={filters.description || ''}
                onChange={(e) => handleFilter('description', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterListIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 250 }}
              />
              <TextField
                size="small"
                placeholder="Filter by amount..."
                value={filters.amount || ''}
                onChange={(e) => handleFilter('amount', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterListIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                size="small"
                placeholder="Filter by confidence (e.g., >70)"
                value={filters.confidence || ''}
                onChange={(e) => handleFilter('confidence', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterListIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 180 }}
              />
            </Box>

            {loadingPredictions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredPredictions.length === 0 ? (
              <Alert severity="info">
                {predictions.length === 0 
                  ? 'No predicted matches found. All transactions and expenses may already be linked, or there are no matching items.'
                  : 'No predictions match the current filters.'}
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={FIT_TABLE_SX}>
                  <FitColgroup
                    cols={[
                      { key: 'confidence', pct: 10 },
                      { key: 'date', pct: 11 },
                      { key: 'match_reasons', pct: 22 },
                      { key: 'transaction' },
                      { key: 'expense', pct: 22 },
                      { key: 'actions', pct: 13 },
                    ]}
                  />
                  <TableHead>
                    <TableRow>
                      <SortableHeader sortKey="confidence">Confidence</SortableHeader>
                      <SortableHeader sortKey="date">Date</SortableHeader>
                      <TableCell sx={{ fontWeight: 'bold' }}>Match Reasons</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Transaction</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Expense</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPredictions.map((prediction) => {
                      const txMerchant = effectiveMerchantName(prediction.transaction);
                      return (
                      <TableRow key={prediction.uid} hover>
                        <TableCell>
                          <Chip 
                            label={`${(prediction.confidence_score * 100).toFixed(0)}%`}
                            color={prediction.confidence_score >= 0.7 ? 'success' : prediction.confidence_score >= 0.5 ? 'warning' : 'default'}
                            size="small"
                            sx={fitChipSx}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={fitTruncateSx}>
                            {formatDate(prediction.transaction?.completed_at || prediction.transaction?.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            {prediction.match_reasons?.map((reason: string, idx: number) => (
                              <Chip key={idx} label={reason} size="small" title={reason} sx={{ ...fitChipSx, mr: 0.5, mb: 0.5 }} />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium" sx={fitTruncateSx} title={prediction.transaction?.description || ''}>
                            {prediction.transaction?.description || 'No description'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={fitTruncateSx}>
                            {formatAmount(prediction.transaction?.amount, prediction.transaction?.currency)}
                          </Typography>
                          {txMerchant ? (
                            <Typography variant="caption" color="text.secondary" sx={fitTruncateSx} title={txMerchant}>
                              {txMerchant}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium" sx={fitTruncateSx} title={prediction.expense?.description || ''}>
                            {prediction.expense?.description || 'No description'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={fitTruncateSx}>
                            {formatAmount(prediction.expense?.spent_amount_value, prediction.expense?.spent_amount_currency)}
                          </Typography>
                          {prediction.expense?.merchant && (
                            <Typography variant="caption" color="text.secondary" sx={fitTruncateSx} title={prediction.expense.merchant}>
                              {prediction.expense.merchant}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell className="fit-table-no-clip">
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<LinkIcon />}
                            onClick={() => confirmMatch(prediction)}
                          >
                            Confirm
                          </Button>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Collapse>
      </Card>

      {/* Confirmed Links Section */}
      <Card id="confirmed">
        <Box sx={{ p: 2, backgroundColor: 'grey.50', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={() => toggleSection('confirmed')}
              size="small"
            >
              {expandedSections.confirmed ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
              Confirmed Links ({summary.totalConfirmed})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
              - Manually confirmed matches between transactions and expenses
            </Typography>
          </Box>
        </Box>
        <Collapse in={expandedSections.confirmed}>
          <Box sx={{ p: 2 }}>
            {filteredConfirmedLinks.length === 0 ? (
              <Alert severity="info">
                No confirmed links yet. Confirm matches above to create links.
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={FIT_TABLE_SX}>
                  <FitColgroup
                    cols={[
                      { key: 'uid', pct: 12 },
                      { key: 'transaction' },
                      { key: 'expense', pct: 27 },
                      { key: 'confidence', pct: 11 },
                      { key: 'confirmed_at', pct: 15 },
                      { key: 'actions', pct: 8 },
                    ]}
                  />
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>UID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Transaction</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Expense</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Confidence</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Confirmed At</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredConfirmedLinks.map((link) => (
                      <TableRow key={link.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ ...fitTruncateSx, fontFamily: 'monospace', fontSize: '0.75rem' }} title={link.uid || ''}>
                            {link.uid?.substring(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={fitTruncateSx} title={link.transaction_revolut_id || ''}>
                            {link.transaction_revolut_id || 'N/A'}
                          </Typography>
                          {link.transaction && (
                            <Typography variant="caption" color="text.secondary" sx={fitTruncateSx} title={link.transaction.description || ''}>
                              {link.transaction.description || 'No description'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={fitTruncateSx} title={link.expense_revolut_id || ''}>
                            {link.expense_revolut_id || 'N/A'}
                          </Typography>
                          {link.expense && (
                            <Typography variant="caption" color="text.secondary" sx={fitTruncateSx} title={link.expense.description || ''}>
                              {link.expense.description || 'No description'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {link.confidence_score ? `${(link.confidence_score * 100).toFixed(0)}%` : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {link.confirmed_at ? formatDateTime(link.confirmed_at) : 'N/A'}
                        </TableCell>
                        <TableCell className="fit-table-no-clip">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this link?')) {
                                try {
                                  await api.delete(`/api/revolut/transaction-expense-links/${link.id}`);
                                  await fetchConfirmedLinks();
                                  await fetchPredictions();
                                } catch (err: any) {
                                  console.error('Error deleting link:', err);
                                  setLinkingError(err.response?.data?.error || 'Failed to delete link');
                                }
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Collapse>
      </Card>
    </Box>
  );
};

export default RevolutLinking;
